import { useCallback, useEffect, useState } from 'react'

/**
 * Roept `onReach` aan zodra het teruggegeven element in beeld komt. Bedoeld
 * voor een lijst die pagina voor pagina bijlaadt: hang de ref onderaan de
 * lijst.
 *
 * Bewust niet `useInView`: die legt de waarnemer aan in een effect dat alleen
 * op zijn instellingen afgaat. Zolang de lijst nog laadt bestaat de markering
 * nog niet, het effect vindt dan geen element, en het draait daarna niet meer —
 * de waarnemer wordt dus nooit aangelegd en er laadt niets bij. Hier is de ref
 * een functie, zodat het effect opnieuw draait op het moment dat het element
 * daadwerkelijk in de DOM verschijnt.
 *
 * Zonder IntersectionObserver (jsdom in de tests, hele oude browsers) doet de
 * haak niets. De knop 'Meer laden' naast de markering blijft dan de weg om
 * verder te lezen — het bijladen mag nooit de enige manier zijn.
 *
 * @param {() => void} onReach - aangeroepen zodra de markering in beeld komt
 * @param {boolean} enabled - uit zetten zodra er niets meer bij te laden valt
 * @param {string} [rootMargin] - hoeveel eerder de markering al meetelt
 * @returns {(node: Element | null) => void} ref om op het laatste element te zetten
 */
const useInfiniteScrollSentinel = (onReach, enabled, rootMargin = '300px') => {

    const [node, setNode] = useState(/** @type {Element | null} */ (null))

    const ref = useCallback((element) => { setNode(element) }, [])

    useEffect(() => {

        if (!node || !enabled) return undefined
        if (typeof IntersectionObserver === 'undefined') return undefined

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) onReach()
        }, { rootMargin })

        observer.observe(node)
        return () => observer.disconnect()

    }, [node, enabled, onReach, rootMargin])

    return ref
}

export default useInfiniteScrollSentinel
